import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import ProfileImage from '@components/_common/profile-image/ProfileImage';
import LikesListModal from '@components/check-in-posts/LikesListModal';
import SnippetMoreModal from '@components/check-in-posts/SnippetMoreModal';
import CommentBottomSheet from '@components/comments/comment-bottom-sheet/CommentBottomSheet';
import { Z_INDEX } from '@constants/layout';
import { Colors, Layout, SvgIcon, Typo } from '@design-system';
import { useTrackEvent } from '@hooks/useTrackEvent';
import { CheckInPost, CheckInPostStory, CheckInPostVisibility } from '@models/checkInPost';
import { useBoundStore } from '@stores/useBoundStore';
import {
  deleteCheckInPost,
  getCheckInPost,
  getUserCheckInPosts,
  readCheckInPosts,
  togglePinCheckInPost,
  updateCheckInPostVisibility,
} from '@utils/apis/checkInPost';
import { deleteLike, postLike } from '@utils/apis/likes';
import { convertTimeDiffByString } from '@utils/timeHelpers';

const STORY_DURATION_MS = 5000;

interface CheckInPostViewerProps {
  story: CheckInPostStory;
  /** When true, fetches all posts from the author and shows a progress bar. */
  enableMultiStory?: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  /** Called after a successful pin toggle or pin_visibility change so the
   * parent (e.g. stories strip) can re-fetch and resort highlights. */
  onPinChange?: (post: CheckInPost) => void;
}

function CheckInPostViewer({
  story,
  enableMultiStory,
  onClose,
  onPrev,
  onNext,
  onPinChange,
}: CheckInPostViewerProps) {
  const [t] = useTranslation('translation', { keyPrefix: 'check_in_post' });
  const navigate = useNavigate();
  const [post, setPost] = useState<CheckInPost | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [inputFocus, setInputFocus] = useState(false);
  const [likeId, setLikeId] = useState<number | null>(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [moreSnippet, setMoreSnippet] = useState<CheckInPostStory | null>(null);

  // Multi-story: fetch all posts from the author and navigate internally
  const [userPosts, setUserPosts] = useState<CheckInPostStory[]>([]);
  const [storyIndex, setStoryIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const startTimeRef = useRef(0);
  const remainingRef = useRef(STORY_DURATION_MS);
  const pressStartRef = useRef(0);

  const { myProfile, openToast } = useBoundStore(
    useShallow((state) => ({ myProfile: state.myProfile, openToast: state.openToast })),
  );
  const trackEvent = useTrackEvent();
  // Per-slide dwell: timestamp at which the current story slide started
  // showing. Flushed every time storyIndex changes AND on viewer close.
  // Tells us how long a viewer actually looked at each individual story
  // — distinct from the auto-advance timer (which only knows the slide
  // is on-screen, not whether the user is engaged).
  const slideStartedAtRef = useRef<number>(Date.now());
  const previousIndexRef = useRef<number>(0);
  // Tracks whether the viewer was opened (single-story or multi-story)
  // so the unmount cleanup can fire `_closed` with the final index.
  const openedRef = useRef(false);
  const lastIndexReachedRef = useRef(0);

  // Fire `_opened` once per viewer mount cycle. is_self flags whether the
  // user is viewing their own stories (e.g. preview from MyPage), which is
  // an analytics-different context from viewing a friend's.
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    trackEvent('check_in_story_opened', {
      author_id: story.author_detail.id,
      is_self: story.author_detail.id === myProfile?.id ? 'true' : 'false',
      multi_story: enableMultiStory ? 'true' : 'false',
    });
    slideStartedAtRef.current = Date.now();
    previousIndexRef.current = 0;
    return () => {
      // Final slide dwell + close summary. last_index reflects how deep
      // the user got into the story sequence (0-indexed).
      const dwellMs = Date.now() - slideStartedAtRef.current;
      if (dwellMs >= 500) {
        trackEvent('check_in_story_slide_dwell', {
          author_id: story.author_detail.id,
          slide_index: previousIndexRef.current,
          duration_ms: dwellMs,
        });
      }
      trackEvent('check_in_story_closed', {
        author_id: story.author_detail.id,
        last_index: lastIndexReachedRef.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slide change → flush previous slide's dwell + start fresh timer.
  useEffect(() => {
    if (storyIndex === previousIndexRef.current) return;
    const dwellMs = Date.now() - slideStartedAtRef.current;
    if (dwellMs >= 500) {
      trackEvent('check_in_story_slide_dwell', {
        author_id: story.author_detail.id,
        slide_index: previousIndexRef.current,
        duration_ms: dwellMs,
      });
    }
    previousIndexRef.current = storyIndex;
    if (storyIndex > lastIndexReachedRef.current) {
      lastIndexReachedRef.current = storyIndex;
    }
    slideStartedAtRef.current = Date.now();
  }, [storyIndex, story.author_detail.id, trackEvent]);

  useEffect(() => {
    if (!enableMultiStory) {
      setUserPosts([]);
      setStoryIndex(0);
      return;
    }
    let cancelled = false;
    setUserPosts([]);
    setStoryIndex(0);
    getUserCheckInPosts(story.author_detail.id)
      .then((data) => {
        if (cancelled) return;
        const expiryMs = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const recent = (data.results ?? []).filter(
          (s) => now - new Date(s.created_at).getTime() < expiryMs,
        );
        const sorted = [...recent].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
        const firstUnread = sorted.findIndex((s) => !s.current_user_read);
        setStoryIndex(firstUnread >= 0 ? firstUnread : 0);
        setUserPosts(sorted);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enableMultiStory, story.author_detail.id]);

  const multiStoryLoading = !!enableMultiStory && userPosts.length === 0;

  const currentStory =
    enableMultiStory && userPosts.length > 0 && storyIndex < userPosts.length
      ? userPosts[storyIndex]
      : story;

  const isOwn = myProfile?.id === currentStory.author_detail.id;

  const isPinned = post?.is_pinned ?? currentStory.is_pinned;
  const postVisibility = post?.visibility ?? currentStory.visibility;
  const isLive = Date.now() - new Date(currentStory.created_at).getTime() < 86400000;
  const caption = post?.caption ?? currentStory.caption;

  const [showLikes, setShowLikes] = useState(false);

  // Reset timer when story changes
  useEffect(() => {
    remainingRef.current = STORY_DURATION_MS;
  }, [storyIndex, story.author_detail.id]);

  const handleHoldStart = useCallback(() => {
    if (!enableMultiStory || multiStoryLoading) return;
    pressStartRef.current = Date.now();
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    setPaused(true);
  }, [enableMultiStory, multiStoryLoading]);

  const handleHoldEnd = useCallback(() => {
    setPaused(false);
  }, []);

  const refreshPost = () => {
    getCheckInPost(currentStory.id)
      .then((data) => setPost(data))
      .catch(() => {});
  };

  const openComments = (e: MouseEvent) => {
    e.stopPropagation();
    setShowComments(true);
  };

  const handleLikeToggle = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!post || isOwn || likeBusy) return;
    setLikeBusy(true);
    try {
      if (likeId) {
        await deleteLike(likeId, (msg) => openToast({ message: msg }));
        setLikeId(null);
      } else {
        const res = await postLike({ target_id: post.id, target_type: 'CheckInPost' }, (msg) =>
          openToast({ message: msg }),
        );
        setLikeId(res.id);
      }
      refreshPost();
    } finally {
      setLikeBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    getCheckInPost(currentStory.id)
      .then((data) => {
        if (!cancelled) setPost(data);
      })
      .catch(() => {
        if (!cancelled) setPost(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStory.id]);

  useEffect(() => {
    setLikeId(post?.current_user_like_id ?? null);
  }, [post?.current_user_like_id]);

  // Mark current story as read
  useEffect(() => {
    if (!currentStory.id || multiStoryLoading) return;
    readCheckInPosts([currentStory.id]).catch(() => {});
  }, [currentStory.id, multiStoryLoading]);

  const overlayOpen = showComments || !!moreSnippet || showLikes;

  // Snapshot remaining time when an overlay opens
  useEffect(() => {
    if (overlayOpen && enableMultiStory) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    }
  }, [overlayOpen, enableMultiStory]);

  // Auto-advance timer for multi-story mode
  useEffect(() => {
    if (!enableMultiStory || multiStoryLoading || overlayOpen || paused) return;

    startTimeRef.current = Date.now();

    const timer = setTimeout(() => {
      remainingRef.current = STORY_DURATION_MS;
      if (storyIndex < userPosts.length - 1) {
        setStoryIndex((prev) => prev + 1);
      } else if (onNext) {
        onNext();
      } else {
        onClose();
      }
    }, remainingRef.current);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMultiStory, multiStoryLoading, storyIndex, userPosts.length, overlayOpen, paused]);

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleLeft = (e: MouseEvent) => {
    e.stopPropagation();
    if (enableMultiStory && Date.now() - pressStartRef.current > 200) return;
    // Manual advance via tap — distinct from auto-advance (5s timer).
    // Manual = user impatient, auto = passive viewing. Different signals.
    trackEvent('check_in_story_advanced', {
      direction: 'prev',
      method: 'tap',
      author_id: story.author_detail.id,
    });
    if (enableMultiStory && storyIndex > 0) {
      remainingRef.current = STORY_DURATION_MS;
      setStoryIndex(storyIndex - 1);
    } else {
      onPrev?.();
    }
  };

  const handleRight = (e: MouseEvent) => {
    e.stopPropagation();
    if (enableMultiStory && Date.now() - pressStartRef.current > 200) return;
    trackEvent('check_in_story_advanced', {
      direction: 'next',
      method: 'tap',
      author_id: story.author_detail.id,
    });
    if (enableMultiStory && storyIndex < userPosts.length - 1) {
      remainingRef.current = STORY_DURATION_MS;
      setStoryIndex(storyIndex + 1);
    } else if (onNext) {
      onNext();
    } else {
      onClose();
    }
  };

  const handlePinToggle = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!post || !isOwn || pinBusy) return;
    setPinBusy(true);
    try {
      const updated = await togglePinCheckInPost(post.id);
      setPost(updated);
      onPinChange?.(updated);
    } finally {
      setPinBusy(false);
    }
  };

  const handleMoreDelete = async (snippet: CheckInPostStory) => {
    await deleteCheckInPost(snippet.id);
    onClose();
  };

  const handleMoreVisibility = async (snippet: CheckInPostStory, v: CheckInPostVisibility) => {
    const updated = await updateCheckInPostVisibility(snippet.id, v);
    setPost(updated);
    onPinChange?.(updated);
  };

  return createPortal(
    <Backdrop onClick={handleBackdropClick}>
      <Card
        onClick={(e) => e.stopPropagation()}
        onPointerDown={handleHoldStart}
        onPointerUp={handleHoldEnd}
        onPointerCancel={handleHoldEnd}
      >
        {enableMultiStory && userPosts.length > 0 && (
          <ProgressBar>
            {userPosts.map((p, idx) => (
              <ProgressSegment
                key={`${p.id}-${idx < storyIndex ? 'v' : idx === storyIndex ? 'a' : 'u'}`}
                $state={idx < storyIndex ? 'viewed' : idx === storyIndex ? 'active' : 'unseen'}
                $paused={paused || overlayOpen}
              />
            ))}
          </ProgressBar>
        )}
        <Header>
          <AuthorLink
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              navigate(`/users/${currentStory.author_detail.username}`);
            }}
            style={{ cursor: 'pointer' }}
          >
            <ProfileImage
              imageUrl={currentStory.author_detail.profile_image}
              username={currentStory.author_detail.username}
              size={32}
            />
            <Typo type="label-large" color="WHITE" bold>
              {currentStory.author_detail.username}
            </Typo>
            <Typo type="label-large" color="LIGHT_GRAY">
              {convertTimeDiffByString({ day: new Date(currentStory.created_at) })}
            </Typo>
          </AuthorLink>
          <Layout.FlexRow alignItems="center" gap={8}>
            {isOwn && (
              <PinButton
                type="button"
                aria-label={(isPinned ? t('unpin') : t('pin')) ?? ''}
                onClick={handlePinToggle}
                disabled={pinBusy || !post}
              >
                <SvgIcon
                  name={isPinned ? 'pin_filled' : 'pin_empty'}
                  size={24}
                  color={isPinned ? 'PRIMARY' : 'WHITE'}
                />
              </PinButton>
            )}
            {isOwn && (
              <PinButton
                type="button"
                aria-label="more"
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreSnippet(currentStory);
                }}
              >
                <SvgIcon name="dots_menu" size={24} color="WHITE" />
              </PinButton>
            )}
            <CloseBtn type="button" onClick={onClose}>
              ×
            </CloseBtn>
          </Layout.FlexRow>
        </Header>

        {isOwn && (isPinned || isLive) && postVisibility === 'close_friends' && (
          <PinVisibilityRow>
            <VisibilityLabel>
              <SvgIcon name="eye" size={16} color="LIGHT_GRAY" />
              <Typo type="label-medium" color="LIGHT_GRAY">
                {t('visibility_close_friends')}
              </Typo>
            </VisibilityLabel>
          </PinVisibilityRow>
        )}

        {!multiStoryLoading && currentStory.image_url && (
          <ImageStage>
            <StoryImage key={currentStory.id} src={currentStory.image_url} alt="daily snippet" />
          </ImageStage>
        )}

        {!multiStoryLoading && caption && (
          <Caption>
            <Typo type="body-medium" color="WHITE">
              {caption}
            </Typo>
          </Caption>
        )}

        <Footer>
          <ActionPill onClick={(e) => e.stopPropagation()}>
            {isOwn ? (
              !!post?.like_count && (
                <PillIconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLikes(true);
                  }}
                  aria-label="likes"
                >
                  <svg width="22" height="22" viewBox="0 0 23 20" fill="none">
                    <path
                      d="M16.2295 1C17.837 1 19.1423 1.55605 20.043 2.45508C20.9434 3.354 21.4999 4.65601 21.5 6.25879C21.5 7.99804 20.8221 9.45583 19.7305 10.8164C18.6198 12.2007 17.1314 13.4249 15.5654 14.708C14.1175 15.8939 12.5304 17.1927 11.2979 18.6494C11.2913 18.6571 11.2828 18.6592 11.2783 18.6592H11.2197C11.2142 18.6592 11.2056 18.6559 11.1992 18.6484C9.96865 17.1918 8.37974 15.8944 6.93457 14.709H6.93359C5.36693 13.4252 3.87898 12.1999 2.76855 10.8154C1.67733 9.45489 1 7.99764 1 6.25879C1.00007 4.65601 1.55646 3.35398 2.45703 2.45508C3.35778 1.55605 4.66338 1 6.27148 1C7.97247 1.00006 9.54511 2.24739 10.5244 3.27246L11.248 4.0293L11.9707 3.27246C12.9492 2.24821 14.5269 1.00008 16.2295 1Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  <Typo type="label-large" color="WHITE">
                    {post.like_count}
                  </Typo>
                </PillIconButton>
              )
            ) : (
              <>
                <PillIconButton
                  type="button"
                  onClick={handleLikeToggle}
                  disabled={likeBusy || !post}
                  aria-label="like"
                >
                  {likeId ? (
                    <svg width="22" height="22" viewBox="0 0 22.5 22" fill={Colors.PRIMARY}>
                      <path d="M16.2297 2.30029C14.086 2.30029 12.2543 3.82825 11.248 4.88166C10.2416 3.82825 8.41398 2.30029 6.27136 2.30029C2.5783 2.30029 0 4.8745 0 8.55939C0 12.6196 3.20216 15.2439 6.3 17.7824C7.7625 18.982 9.27614 20.2216 10.4369 21.5961C10.6323 21.8262 10.9186 21.9592 11.2193 21.9592H11.2786C11.5803 21.9592 11.8657 21.8252 12.06 21.5961C13.2228 20.2216 14.7355 18.981 16.199 17.7824C19.2958 15.245 22.5 12.6206 22.5 8.55939C22.5 4.8745 19.9217 2.30029 16.2297 2.30029Z" />
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 23 20" fill="none">
                      <path
                        d="M16.2295 1C17.837 1 19.1423 1.55605 20.043 2.45508C20.9434 3.354 21.4999 4.65601 21.5 6.25879C21.5 7.99804 20.8221 9.45583 19.7305 10.8164C18.6198 12.2007 17.1314 13.4249 15.5654 14.708C14.1175 15.8939 12.5304 17.1927 11.2979 18.6494C11.2913 18.6571 11.2828 18.6592 11.2783 18.6592H11.2197C11.2142 18.6592 11.2056 18.6559 11.1992 18.6484C9.96865 17.1918 8.37974 15.8944 6.93457 14.709H6.93359C5.36693 13.4252 3.87898 12.1999 2.76855 10.8154C1.67733 9.45489 1 7.99764 1 6.25879C1.00007 4.65601 1.55646 3.35398 2.45703 2.45508C3.35778 1.55605 4.66338 1 6.27148 1C7.97247 1.00006 9.54511 2.24739 10.5244 3.27246L11.248 4.0293L11.9707 3.27246C12.9492 2.24821 14.5269 1.00008 16.2295 1Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </PillIconButton>
                {!!post?.like_count && (
                  <Typo type="label-large" color="WHITE">
                    {post.like_count}
                  </Typo>
                )}
              </>
            )}
            <PillIconButton
              type="button"
              onClick={openComments}
              aria-label={t('comment') ?? ''}
              disabled={!post}
            >
              <svg width="22" height="22" viewBox="0 0 23 23" fill="none">
                <path
                  d="M3.1 2H19.9C20.5075 2 21 2.49249 21 3.1V20.1L16.4004 16.8H3.1C2.49249 16.8 2 16.3075 2 15.7V3.1C2 2.49249 2.49249 2 3.1 2Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  fill="none"
                />
                <path
                  d="M12.4585 13.4167H10.5418V10.5417H7.66683V8.625H10.5418V5.75H12.4585V8.625H15.3335V10.5417H12.4585V13.4167Z"
                  fill="currentColor"
                />
              </svg>
            </PillIconButton>
            {!!post?.comment_count && (
              <PillIconButton type="button" onClick={openComments} disabled={!post}>
                <Typo type="label-large" color="WHITE">
                  {post.comment_count}
                </Typo>
              </PillIconButton>
            )}
          </ActionPill>
        </Footer>

        {(enableMultiStory ? storyIndex > 0 || !!onPrev : !!onPrev) && (
          <NavZone $side="left" onClick={handleLeft} />
        )}
        <NavZone $side="right" onClick={handleRight} />
      </Card>

      {post && (
        <CommentBottomSheet
          postType="CheckInPost"
          post={post}
          visible={showComments}
          inputFocus={inputFocus}
          setInputFocus={setInputFocus}
          closeBottomSheet={() => setShowComments(false)}
        />
      )}

      <LikesListModal
        postId={showLikes && post ? post.id : null}
        onClose={() => setShowLikes(false)}
      />

      <SnippetMoreModal
        snippet={moreSnippet}
        onClose={() => setMoreSnippet(null)}
        onDelete={handleMoreDelete}
        onChangeVisibility={handleMoreVisibility}
      />
    </Backdrop>,
    document.body,
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.92);
  z-index: ${Z_INDEX.MODAL_CONTAINER - 1};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  height: 100%;
  max-height: 100vh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #1c1c1e;
  padding-top: calc(72px + env(safe-area-inset-top, 0px));
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-top: calc(16px + env(safe-area-inset-top));
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent);
`;

const PinButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PinVisibilityRow = styled.div`
  position: absolute;
  top: calc(64px + env(safe-area-inset-top));
  right: 16px;
  display: flex;
  gap: 6px;
  z-index: 3;
`;

const VisibilityLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 9999px;
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
`;

// VisibilityToggle removed — visibility changes now go through the three-dot menu

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${Colors.WHITE};
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px;
`;

const ImageStage = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StoryImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const Caption = styled.div`
  padding: 16px;
  text-align: center;
`;

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  display: flex;
  justify-content: flex-start;
  z-index: 2;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
`;

const ActionPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 9999px;
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
`;

const PillIconButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: ${Colors.WHITE};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavZone = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 25%;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  cursor: pointer;
  z-index: 1;
`;

const ProgressBar = styled.div`
  position: absolute;
  top: calc(env(safe-area-inset-top) + 6px);
  left: 8px;
  right: 8px;
  display: flex;
  gap: 3px;
  z-index: 4;
`;

const ProgressSegment = styled.div<{
  $state: 'viewed' | 'active' | 'unseen';
  $paused: boolean;
}>`
  flex: 1;
  height: 2px;
  border-radius: 1px;
  background-color: rgba(255, 255, 255, 0.3);
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 1px;
    background-color: rgba(255, 255, 255, 0.9);
    transform-origin: left;
    ${({ $state, $paused }) => {
      if ($state === 'viewed') return 'transform: scaleX(1);';
      if ($state === 'unseen') return 'transform: scaleX(0);';
      return `
        animation: fillSegment ${STORY_DURATION_MS}ms linear forwards;
        animation-play-state: ${$paused ? 'paused' : 'running'};
      `;
    }}
  }

  @keyframes fillSegment {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`;

const AuthorLink = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
`;

export default CheckInPostViewer;
